import { useTranslation } from 'react-i18next';
import { Globe, Lock } from 'lucide-react';

interface VisibilityChipProps {
  isPublic: boolean;
  onToggle: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Current visibility shown as a muted label, next to the flip action styled as a
 * quiet gray button — so the button reads as the tappable thing without the
 * color noise of a brand-tinted action. Both directions use the same treatment.
 * One element instead of a separate badge + button, matched by the Arty chip.
 */
export function VisibilityChip({ isPublic, onToggle, disabled = false, className }: VisibilityChipProps) {
  const { t } = useTranslation('artwork');
  const StateIcon = isPublic ? Globe : Lock;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-label={isPublic ? t('image-manager.visibility.make-private') : t('image-manager.visibility.make-public')}
      className={`group inline-flex items-center justify-between gap-1.5 whitespace-nowrap text-[11px] font-semibold disabled:opacity-60 ${
        className ?? ''
      }`}
    >
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-zinc-400">
        <StateIcon className="size-3.5 shrink-0" />
        {isPublic ? t('image-manager.visibility.on-profile') : t('image-manager.visibility.private')}
      </span>
      <span className="shrink-0 whitespace-nowrap rounded-full bg-zinc-100 px-2 py-1 text-zinc-600 transition-colors group-hover:bg-zinc-200">
        {isPublic ? t('image-manager.visibility.make-private') : t('image-manager.visibility.make-public')}
      </span>
    </button>
  );
}
